import React, { memo, useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { Alert, App, Card, Typography } from 'antd';

import { Field, Form, Formik } from 'formik';
import type { FormikProps } from 'formik';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import MyModal from 'v2/components/Shared/MyModal';
import NoDangerouslyHTML from 'v2/components/Shared/NoDangerouslyHTML/index';
import { logError } from 'v2/helpers/rxjs-operators/logError';
import useGetFingerprint from 'v2/hooks/useGetFingerprint';
import useGetOriginalUser from 'v2/hooks/useGetOriginalUser';
import useGetUser from 'v2/hooks/useGetUser';
import type IProfile from 'v2/interfaces/profile';
import type { IModalFiscalForm } from 'v2/interfaces/terms';
import { profileFactory } from 'v2/services/profile';
import { termsFactory } from 'v2/services/terms';

import Address from './address';
import ModalFiscaSchema from './validation';
import { CheckboxFormField } from '../FormFields/CheckboxFormField';
import { TextFormField } from '../FormFields/TextFormField';
import ModalMinor from '../ModalMinor';

const TERMS_OF_USE_URL = 'https://eduzz.com/termos/';

interface ModalFiscalProps {
  opened: boolean;
  canSkip?: boolean;
  profile: IProfile;
  subtitle?: string;
  onClose: () => void;
  onComplete: (profile?: IProfile) => void;
  onSkip?: () => void;
  returnDataOnComplete?: boolean;
}

const ModalFiscal = memo(
  ({
    opened,
    onClose,
    onComplete,
    onSkip,
    profile,
    subtitle,
    returnDataOnComplete,
    canSkip = true
  }: ModalFiscalProps) => {
    const { message } = App.useApp();
    const user = useGetUser();
    const originalUser = useGetOriginalUser();
    const fingerprint = useGetFingerprint();
    const history = useHistory();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [modalMinor, setModalMinor] = useState(false);
    const openModalMinor = useCallback(() => setModalMinor(true), []);
    const closeModalMinor = useCallback(() => setModalMinor(false), []);
    const { search } = useLocation();
    const isAccessByControlAccess = user?.id !== originalUser?.id;

    const [term] = useObservable(() => termsFactory.loadFiscalTerm().pipe(logError()), []);

    const [updateUserAction] = useCallbackObservable(
      (model: IModalFiscalForm) => {
        setLoading(true);
        return profileFactory.updateProfile({ ...model, fingerprint }).pipe(
          tap(res => {
            message.success('Perfil alterado com sucesso.');
            setLoading(false);
            const searchParams = new URLSearchParams(search);

            if (returnDataOnComplete && res && res.data && res.data.profile) {
              onComplete(res.data.profile);

              if (searchParams.has('backTo')) {
                window.location.replace(searchParams.get('backTo'));
              }

              return;
            }

            onComplete();

            if (searchParams.has('backTo')) {
              window.location.replace(searchParams.get('backTo'));
            }
          }),
          catchError(err => {
            if (err == 'DOC_UNDER_AGE') {
              openModalMinor();
              return of(true);
            }

            if (err == 'ON_BLACKLIST') {
              setLoading(false);
              message.error('Não é possível alterar o perfil. Entre em contato com o suporte.');
              return of(true);
            }

            setLoading(false);
            message.error('Erro ao atualizar perfil. Tente novamente!');
            return of(true);
          })
        );
      },
      [fingerprint, message, onComplete, openModalMinor, returnDataOnComplete, search]
    );

    const handleOnClose = () => {
      if (canSkip) {
        onSkip?.();
        return onClose();
      }

      if (location.pathname === '/producer/dashboard') {
        history.push('/');
        setTimeout(() => {
          history.push('/producer/dashboard');
        }, 0);
        return;
      }

      history.push('/producer/dashboard');
    };

    const initialValues = {
      address_zip_code: profile?.address_zip_code || '',
      address_street: profile?.address_street || '',
      address_neighborhood: profile?.address_neighborhood || '',
      address_state: profile?.address_state || '',
      address_number: profile?.address_number?.toString() || '',
      address_complement: profile?.address_complement || '',
      address_city_name: profile?.address_city_name || '',
      document_number: profile?.document_number || '',
      type: profile?.type || '',
      address_country: 'Brasil',
      fiscal_responsibility: false,
      producer_affiliate_terms: false
    };

    function getModalContent(
      formikProps: FormikProps<IModalFiscalForm>,
      getError: (field: keyof IModalFiscalForm) => any
    ) {
      if (isAccessByControlAccess) {
        return (
          <div className='flex flex-col gap-4'>
            <Alert
              type='info'
              showIcon
              message='O titular desta conta ainda precisa concluir um passo importante'
              description='Preencher alguns dados obrigatórios e aceitar nossos termos.'
            />

            <Typography.Paragraph className='mb-0'>
              Por isso,{' '}
              <Typography.Text strong>
                algumas funcionalidades estão temporariamente indisponíveis para você e para outros usuários com acesso
                a esta conta.
              </Typography.Text>
            </Typography.Paragraph>

            <div className='rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'>
              <Typography.Text strong className='mb-2 block'>
                O que precisa ser feito pelo titular da conta:
              </Typography.Text>
              <ul className='mb-0 list-disc pl-5'>
                <li>Preencher os dados cadastrais obrigatórios (Razão Social, CPF/CNPJ, Endereço e Celular);</li>
                <li>Aceitar o Termo de Uso da Eduzz.</li>
              </ul>
            </div>

            <Typography.Paragraph className='mb-0'>
              Assim que esse processo for concluído, tudo volta a funcionar normalmente para você e para todos os
              usuários vinculados a essa conta.
            </Typography.Paragraph>

            <Typography.Paragraph className='mb-0'>
              Se quiser, você pode avisar o titular da conta para agilizar esse processo. Estamos aqui para garantir
              mais segurança e transparência nas suas operações.
            </Typography.Paragraph>
          </div>
        );
      }

      return (
        <Form className='flex flex-col gap-4'>
          <Typography.Text type='secondary'>Preencha os seguintes dados obrigatórios:</Typography.Text>

          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <Field
              label='CPF/CNPJ'
              name='document_number'
              id='document_number'
              mask='document'
              disabled={Boolean(formikProps.initialValues.document_number)}
              component={TextFormField}
            />

            <Address {...formikProps} getError={getError} />
          </div>

          <div className='flex flex-col gap-2 rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'>
            <Field
              label='Aceito o termo de responsabilidade fiscal'
              name='fiscal_responsibility'
              id='fiscal_responsibility'
              component={CheckboxFormField}
            />
            <Card size='small'>
              <NoDangerouslyHTML html={term?.hint ?? ''} />
            </Card>
          </div>

          <Field
            label={
              <>
                Aceito os{' '}
                <Typography.Link href={TERMS_OF_USE_URL} target='_blank' rel='noopener noreferrer'>
                  termos de uso
                </Typography.Link>
              </>
            }
            name='producer_affiliate_terms'
            id='producer_affiliate_terms'
            component={CheckboxFormField}
          />

          <Typography.Text type='secondary'>
            Essa etapa é importante para garantir segurança jurídica, transparência nas transações e conformidade com
            nossos parceiros financeiros.
          </Typography.Text>
        </Form>
      );
    }

    if (!opened) {
      return null;
    }

    return (
      <>
        <ModalMinor opened={modalMinor} onClose={closeModalMinor} />

        <Formik initialValues={initialValues} onSubmit={updateUserAction} validationSchema={ModalFiscaSchema}>
          {(formikProps: FormikProps<IModalFiscalForm>) => {
            const getError = (field: keyof IModalFiscalForm): any => {
              if (formikProps.touched[field] && formikProps.errors[field]) return formikProps.errors[field];
              return null;
            };

            return (
              <MyModal
                opened={opened}
                submitLoading={loading}
                showCancelButton={canSkip}
                onSubmit={() => {
                  formikProps.validateForm().then(errors => {
                    if (Object.keys(errors).length > 0) {
                      const firstErrorKey = Object.keys(errors)[0];
                      const errorElement = document.getElementById(firstErrorKey);
                      errorElement?.focus();
                      errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }

                    formikProps.submitForm();
                  });
                }}
                onClose={handleOnClose}
                showOk={!isAccessByControlAccess}
                buttonCancel={isAccessByControlAccess ? 'Fechar' : 'Cancelar'}
                title={isAccessByControlAccess ? 'Estamos quase lá!' : 'Complete seu perfil'}
                message={
                  (subtitle ?? isAccessByControlAccess)
                    ? null
                    : 'Você precisa completar seus dados e aceitar nossos termos!'
                }
              >
                {getModalContent(formikProps, getError)}
              </MyModal>
            );
          }}
        </Formik>
      </>
    );
  }
);

export default ModalFiscal;
